/**
 * The CA firm presentation at /help/for-ca-firms.html: a slide deck on a 1600 × 900 stage scaled to the window (arrow keys,
 * F for full screen, O for the list of slides), or one long page on a phone, where a prospect opens a shared link.
 */
import './deck.css';
import { META_RATES, PLAY_STORE_URL, SUPPORT_EMAIL } from '../config';
import { rupees } from '../prices.ts';
import { FIRMS, estimate, type Firm } from './estimate.ts';
import { BASE, FILMS, buildSlides, type Slide, type Visual } from './slides.ts';
import { PANELS } from './panels';

const rates = { marketing: META_RATES.marketing, utility: META_RATES.utility };
const SLIDES = buildSlides(rates);
const PPT = `${BASE}/Jackdesk-for-CA-firms.pptx`;

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const li = (items: string[]) => items.map((p) => `<li>${esc(p)}</li>`).join('');
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const ICONS: Record<string, string> = {
  chat: 'M4 5h16v11H9l-5 4z', ticket: 'M4 7h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4z', team: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7 0a2.5 2.5 0 1 0 0-5M3 19c0-3 3-5 6-5s6 2 6 5m2-5c2 0 4 2 4 4',
  board: 'M4 5h16v14H4zM10 5v14M15 5v14', flow: 'M5 4h6v5H5zM13 15h6v5h-6zM8 9v3h8v3', doc: 'M6 3h9l4 4v14H6zM9 12h7M9 16h7', mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  phone: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM11 18h2', clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2', rupee: 'M7 4h10M7 9h10M7 4c5 0 7 2 7 5s-3 5-7 5l7 7',
  chart: 'M4 20V10M10 20V4M16 20v-7M21 20H3', star: 'M12 3l2.7 5.8 6.3.8-4.6 4.3 1.2 6.2L12 17l-5.6 3.1 1.2-6.2L3 9.6l6.3-.8z',
};
const icon = (n: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="${ICONS[n]}"/></svg>`;

function win(v: Visual): string {
  if (v.phones) return `<div class="phones" role="img" aria-label="${esc(v.alt)}">${v.phones.map((p) => `<div class="phone"><img src="${BASE}/${p}" alt="" loading="lazy" /></div>`).join('')}</div>`;
  const body = v.panel ? `<div class="panel" id="panel-${v.panel}" role="img" aria-label="${esc(v.alt)}">${PANELS[v.panel]()}</div>` : `<img src="${BASE}/${v.shot}" alt="${esc(v.alt)}" loading="lazy" />`;
  return `<figure class="win"><figcaption><i></i><i></i><i></i><span>${esc(v.title)}</span></figcaption>${body}</figure>`;
}

const head = (eyebrow: string, title: string, lede?: string) => `<header class="sh"><span class="eyebrow">${esc(eyebrow)}</span><h2>${esc(title)}</h2>${lede ? `<p class="lede">${esc(lede)}</p>` : ''}</header>`;

function film(f: (typeof FILMS)[number]): string {
  return `<div class="film">
    <div class="film-main">
      <video data-film controls playsinline preload="none" poster="${BASE}/${f.poster}" src="${BASE}/${f.file}" aria-label="${esc(f.title)}"></video>
    </div>
    <aside class="film-side">
      <span class="eyebrow">Film ${f.n} of ${FILMS.length} · ${f.length}</span>
      <h2>${esc(f.title)}</h2>
      <p>${esc(f.about)}</p>
      <h5>Watch for</h5>
      <ul class="ticks light">${li(f.watch)}</ul>
      <h5>Jump to</h5>
      <div class="chapters">${f.chapters.map((c) => `<button data-seek="${c.at}"><span>${clock(c.at)}</span> ${esc(c.label)}</button>`).join('')}</div>
      <div class="speed" role="group" aria-label="Speed">${[1, 1.5, 2].map((s) => `<button data-speed="${s}"${s === 1 ? ' class="on"' : ''}>${s}×</button>`).join('')}</div>
    </aside>
  </div>`;
}

function body(s: Slide): string {
  switch (s.kind) {
    case 'cover':
      return `<div class="cover"><img class="cover-logo" src="/logo.png" alt="Jackdesk" /><span class="eyebrow on-dark">${esc(s.kicker)}</span><h1>${esc(s.title)}</h1><p class="lede">${esc(s.sub)}</p><p class="line">${esc(s.line)}</p></div>`;
    case 'pains':
      return `${head(s.eyebrow, s.title, s.lede)}<div class="pains">${s.items.map((p) => `<article><b>${esc(p.q)}</b><span>${esc(p.a)}</span></article>`).join('')}</div>`;
    case 'cost': {
      const total = s.rows.reduce((n, r) => n + r.today, 0);
      const withIt = s.rows.reduce((n, r) => n + r.with, 0);
      const bars = s.rows.map((r) => `<div class="bar-row" title="${esc(r.task)}: about ${r.today} hours today, ${r.with ? `about ${r.with}` : 'none'} with Jackdesk">
        <span class="bar-l">${esc(r.task)}</span>
        <div class="bar-t"><i class="b-today" style="width:${(r.today / 20) * 100}%"></i><em>${r.today} h</em></div>
        <div class="bar-t"><i class="b-with" style="width:${(r.with / 20) * 100}%"></i><em>${r.with ? `${r.with} h` : 'none'}</em></div>
      </div>`).join('');
      return `<div class="cost">${head(s.eyebrow, s.title, s.lede)}
        <div class="cost-hero"><div><b>${total}</b><span>hours a month today</span></div><i>→</i><div class="good"><b>${Math.ceil(withIt)}</b><span>hours with Jackdesk</span></div></div>
        <div class="bars"><div class="legend"><span><i class="b-today"></i>Today</span><span><i class="b-with"></i>With Jackdesk</span></div>${bars}</div>
        <p class="foot">${esc(s.foot)}</p></div>`;
    }
    case 'flow':
      return `${head(s.eyebrow, s.title, s.lede)}<ol class="flow">${s.steps.map((t, i) => `<li><span>${i + 1}</span><b>${esc(t.t)}</b><small>${esc(t.d)}</small></li>`).join('')}</ol><p class="foot">${esc(s.foot)}</p>`;
    case 'map':
      return `${head(s.eyebrow, s.title, s.lede)}<div class="map">${s.tiles.map((t) => `<a href="#${t.to}" data-go="${t.to}"><span class="ic">${icon(t.icon)}</span><b>${esc(t.t)}</b><small>${esc(t.d)}</small></a>`).join('')}</div>`;
    case 'feature':
      return `<div class="feature${s.flip ? ' flip' : ''}"><div class="f-text">
        <span class="eyebrow">${esc(s.eyebrow)}</span><h2>${esc(s.title)}</h2>
        <p class="today"><b>Today</b> ${esc(s.today)}</p>
        <ul class="ticks">${li(s.points)}</ul>${s.foot ? `<p class="foot">${esc(s.foot)}</p>` : ''}
      </div><div class="f-vis">${win(s.visual)}</div></div>`;
    case 'filmsintro':
      return `${head(s.eyebrow, s.title, s.lede)}<div class="film-cards">${FILMS.map((f) => `<a href="#film${f.n}" data-go="film${f.n}"><img src="${BASE}/${f.poster}" alt="" loading="lazy" /><span class="play">▶</span><b>${esc(f.title)}</b><small>${f.length}</small></a>`).join('')}</div>`;
    case 'film':
      return film(s.film);
    case 'trust':
      return `${head(s.eyebrow, s.title, s.lede)}<div class="trust">${s.cols.map((c) => `<article><b>${esc(c.t)}</b><span>${esc(c.d)}</span></article>`).join('')}</div>
        <div class="mask"><div><small>What the firm has</small><code>${esc(s.before)}</code></div><i>→</i><div class="good"><small>The most AI ever sees</small><code>${esc(s.after)}</code></div></div><p class="foot">${esc(s.foot)}</p>`;
    case 'case':
      return `${head(s.eyebrow, s.title, s.lede)}<div class="stats n${s.stats.length}">${s.stats.map((t) => `<div><b>${esc(t.v)}</b><span>${esc(t.l)}</span></div>`).join('')}</div>
        <div class="case"><article><h5>Before</h5><ul class="dash">${li(s.before)}</ul></article><article class="good"><h5>With Jackdesk</h5><ul class="ticks">${li(s.after)}</ul></article>
        ${s.next ? `<article class="next"><h5>${esc(s.next.t)}</h5><p>${esc(s.next.d)}</p></article>` : ''}</div><p class="foot">${esc(s.foot)}</p>`;
    case 'compare': {
      const cell = (c: string | boolean | null) => (c === true ? '<span class="yes" title="Yes">✓</span>' : c === false ? '<span class="no" title="No">✕</span>' : c === null ? '<span class="na" title="Their page does not say">—</span>' : esc(c));
      return `${head(s.eyebrow, s.title, s.lede)}<div class="tbl-wrap"><table class="compare"><thead><tr><th></th>${s.heads.map((h, i) => `<th${i ? '' : ' class="us"'}>${esc(h)}</th>`).join('')}</tr></thead>
        <tbody>${s.rows.map((r, n) => `<tr class="${n ? '' : 'price'}"><th>${esc(r.label)}</th>${r.cells.map((c, i) => `<td${i ? '' : ' class="us"'}>${cell(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div><p class="foot">${esc(s.foot)}</p>`;
    }
    case 'calc':
      return `<div class="calc-wrap"><header class="sh tight"><span class="eyebrow">${esc(s.eyebrow)}</span><h2>${esc(s.title)}</h2></header><div id="calc"></div><p class="foot">${esc(s.foot)}</p></div>`;
    case 'faq':
      return `${head(s.eyebrow, s.title)}<div class="faq">${s.items.map((f) => `<article><b>${esc(f.q)}</b><span>${esc(f.a)}</span></article>`).join('')}</div>`;
    case 'start':
      return `${head(s.eyebrow, s.title, s.lede)}<ol class="start">${s.steps.map((t, i) => `<li><span>${i + 1}</span><b>${esc(t.t)}</b><small>${esc(t.d)}</small></li>`).join('')}</ol><p class="offer">${esc(s.offer)}</p>`;
    case 'close':
      return `<div class="cover close"><img class="cover-logo" src="/logo.png" alt="Jackdesk" /><h1>${esc(s.title)}</h1><p class="lede">${esc(s.sub)}</p>
        <div class="close-links">${s.links.map((l, i) => `<a class="${i ? '' : 'primary'}" href="${l.href}">${esc(l.t)}</a>`).join('')}<a href="${PLAY_STORE_URL}" target="_blank" rel="noopener">Phone app on Google Play</a></div>
        <p class="line">${esc(SUPPORT_EMAIL)} · jackdesk.com</p></div>`;
  }
}

const dark = (s: Slide) => s.kind === 'cover' || s.kind === 'close' || s.kind === 'film';

const root = document.getElementById('deck')!;
root.innerHTML = `
  <main class="stage" id="stage">${SLIDES.map((s, i) => `<section class="slide k-${s.kind}${dark(s) ? ' dark' : ''}" id="${s.id}" data-i="${i}" aria-label="${esc(s.label)}">
    ${dark(s) ? '' : '<img class="mark" src="/logo.png" alt="" />'}${body(s)}<span class="num">${i + 1}</span></section>`).join('')}</main>
  <nav class="bar" aria-label="Slides">
    <button id="prev" aria-label="Previous slide">‹</button><span id="pos"></span><button id="next" aria-label="Next slide">›</button>
    <button id="list" aria-label="All slides">Slides</button><button id="full" aria-label="Full screen">Full screen</button>
    <a href="${PPT}" download>PowerPoint</a>
  </nav>
  <div class="index" id="index" hidden><div><header><b>All slides</b><button id="close-index" aria-label="Close">✕</button></header>
    <ol>${SLIDES.map((s) => `<li><a href="#${s.id}" data-go="${s.id}">${esc(s.label)}</a></li>`).join('')}</ol></div></div>`;

/* ------------------------------------------------------------------ moving between slides */
const slides = [...root.querySelectorAll<HTMLElement>('.slide')];
const stage = document.getElementById('stage')!;
const pos = document.getElementById('pos')!;
const index = document.getElementById('index')!;
const narrow = window.matchMedia('(max-width: 900px), (orientation: portrait) and (max-width: 1100px)');
let at = 0;

const reading = () => narrow.matches;
function fit() {
  document.body.classList.toggle('read', reading());
  document.body.classList.toggle('present', !reading());
  stage.style.transform = reading() ? '' : `scale(${Math.min(window.innerWidth / 1600, window.innerHeight / 900)})`;
}
function pauseAll() { root.querySelectorAll('video').forEach((v) => v.pause()); }
function show(i: number, push = true) {
  at = Math.max(0, Math.min(slides.length - 1, i));
  slides.forEach((s, n) => s.classList.toggle('on', n === at));
  pauseAll();
  pos.textContent = `${at + 1} / ${slides.length}`;
  if (push) history.replaceState(null, '', `#${slides[at].id}`);
  // The next slide's pictures, so moving on never shows them arriving.
  slides[at + 1]?.querySelectorAll('img').forEach((im) => { im.loading = 'eager'; });
}
function go(id: string) {
  const i = slides.findIndex((s) => s.id === id);
  if (i < 0) return;
  index.hidden = true;
  if (reading()) slides[i].scrollIntoView({ behavior: 'smooth' });
  else show(i);
}
root.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const link = t.closest<HTMLElement>('[data-go]');
  if (link) { e.preventDefault(); go(link.dataset.go!); return; }
  const seek = t.closest<HTMLElement>('[data-seek]');
  if (seek) {
    const v = seek.closest('.film')!.querySelector('video')!;
    v.currentTime = Number(seek.dataset.seek);
    void v.play();
    return;
  }
  const sp = t.closest<HTMLElement>('[data-speed]');
  if (sp) {
    const v = sp.closest('.film')!.querySelector('video')!;
    v.defaultPlaybackRate = v.playbackRate = Number(sp.dataset.speed);
    sp.parentElement!.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b === sp));
  }
});
document.getElementById('prev')!.onclick = () => show(at - 1);
document.getElementById('next')!.onclick = () => show(at + 1);
document.getElementById('list')!.onclick = () => { index.hidden = !index.hidden; };
document.getElementById('close-index')!.onclick = () => { index.hidden = true; };
const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen());
document.getElementById('full')!.onclick = () => { void fullscreen(); };
document.addEventListener('keydown', (e) => {
  const t = e.target as HTMLElement;
  if (reading() || t.matches('input, select, textarea, video')) return;
  if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); show(at + 1); }
  else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); show(at - 1); }
  else if (e.key === 'Home') show(0);
  else if (e.key === 'End') show(slides.length - 1);
  else if (e.key === 'f' || e.key === 'F') void fullscreen();
  else if (e.key === 'o' || e.key === 'O') index.hidden = !index.hidden;
  else if (e.key === 'Escape') index.hidden = true;
});
let touchX: number | null = null;
stage.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
stage.addEventListener('touchend', (e) => {
  if (touchX === null || reading()) return;
  const dx = e.changedTouches[0].clientX - touchX;
  if ((e.target as HTMLElement).closest('input, video')) return;
  if (Math.abs(dx) > 60) show(at + (dx < 0 ? 1 : -1));
  touchX = null;
});
let idle = 0;
const wake = () => { document.body.classList.add('awake'); window.clearTimeout(idle); idle = window.setTimeout(() => document.body.classList.remove('awake'), 2600); };
document.addEventListener('mousemove', wake);

/* ------------------------------------------------------------------ the calculator */
const INPUTS: { key: keyof Firm; label: string; max: number; step: number; group: 'firm' | 'load' }[] = [
  { key: 'staff', label: 'Staff who use it', max: 100, step: 1, group: 'firm' },
  { key: 'clients', label: 'Clients', max: 3000, step: 10, group: 'firm' },
  { key: 'gst', label: 'On monthly GST', max: 2000, step: 10, group: 'firm' },
  { key: 'itr', label: 'Income-tax returns a year', max: 3000, step: 10, group: 'firm' },
  { key: 'updates', label: 'Updates, letters, reminders', max: 10000, step: 50, group: 'load' },
  { key: 'replies', label: 'Replies in the chat', max: 10000, step: 100, group: 'load' },
  { key: 'offers', label: 'Greetings and offers', max: 5000, step: 50, group: 'load' },
  { key: 'numbers', label: 'WhatsApp numbers', max: 10, step: 1, group: 'load' },
];
const TOGGLES: { key: 'teams' | 'workbook' | 'hr'; label: string }[] = [{ key: 'teams', label: 'Team chat' }, { key: 'workbook', label: 'Workbook' }, { key: 'hr', label: 'HR and payroll' }];
const firm: Firm = { ...FIRMS.mid };
const calc = document.getElementById('calc')!;
const field = (i: (typeof INPUTS)[number]) => `<label class="c-f"><span>${i.label}</span><input type="range" min="${i.key === 'staff' || i.key === 'numbers' ? 1 : 0}" max="${i.max}" step="${i.step}" data-k="${i.key}" /><input type="number" min="0" max="${i.max}" data-k="${i.key}" aria-label="${i.label}" /></label>`;
calc.innerHTML = `
  <div class="c-in">
    <div class="presets" role="group" aria-label="Start from">${(Object.keys(FIRMS) as (keyof typeof FIRMS)[]).map((k) => `<button data-preset="${k}"${k === 'mid' ? ' class="on"' : ''}>${FIRMS[k].label}</button>`).join('')}</div>
    <h5>Your firm</h5>${INPUTS.filter((i) => i.group === 'firm').map(field).join('')}
    <h5>WhatsApp, a month</h5>${INPUTS.filter((i) => i.group === 'load').map(field).join('')}
    <h5>Add to Messaging and CA Practice</h5>
    <div class="c-t">${TOGGLES.map((t) => `<label><input type="checkbox" data-t="${t.key}" /> ${t.label}</label>`).join('')}</div>
  </div>
  <div class="c-out" aria-live="polite">
    <div class="c-us"><span class="eyebrow on-dark">Jackdesk, your estimate</span><div class="c-big" id="c-total"></div><div class="c-sub" id="c-sub"></div><ul id="c-lines"></ul></div>
    <div class="c-them"><h5>The same jobs bought separately</h5><ul id="c-stack"></ul><div class="c-save" id="c-save"></div></div>
    <div class="c-hours"><div><b id="c-h"></b><span>hours a month back</span></div><label class="c-hr">Staff cost an hour <input type="number" min="0" max="5000" step="10" data-k="hourly" aria-label="Staff cost an hour in rupees" /></label><div><b id="c-v"></b><span>worth of time a year</span></div></div>
  </div>`;
const num = (v: number) => new Intl.NumberFormat('en-IN').format(v);
function paint() {
  const e = estimate(firm, rates);
  calc.querySelectorAll<HTMLInputElement>('input[data-k]').forEach((i) => { const v = String(firm[i.dataset.k as keyof Firm]); if (i.value !== v) i.value = v; });
  calc.querySelectorAll<HTMLInputElement>('input[data-t]').forEach((i) => { i.checked = firm[i.dataset.t as 'teams']; });
  document.getElementById('c-total')!.innerHTML = `${rupees(e.total)}<small> a year</small>`;
  document.getElementById('c-sub')!.textContent = `about ${rupees(Math.round(e.total / 12))} a month · ${rupees(e.perClientMonth)} a client a month`;
  document.getElementById('c-lines')!.innerHTML = [
    ['Plan', e.plan], ['WhatsApp at Meta’s rate', e.messages], ['Extra storage', e.storage], ['Masked AI, if switched on', e.ai],
  ].map(([l, v]) => `<li><span>${l}</span><b>${rupees(v as number)}</b></li>`).join('') + (e.free ? `<li class="muted"><span>Free messages included</span><b>${rupees(Math.min(e.free, e.messagesBeforeFree))}</b></li>` : '');
  document.getElementById('c-stack')!.innerHTML = e.stack.map((r) => `<li><span>${r.job}<small>${r.tool}${r.reported ? ' (reported)' : ''}</small></span><b>${rupees(r.year)}</b></li>`).join('') + `<li class="sum"><span>Together</span><b>${rupees(e.stackTotal)}</b></li>`;
  document.getElementById('c-save')!.textContent = e.saved > 0 ? `${rupees(e.saved)} less a year, ${e.times >= 2 ? `about ${e.times.toFixed(1)} times` : 'noticeably'} cheaper` : 'About the same as buying them separately';
  document.getElementById('c-h')!.textContent = num(Math.round(e.hoursBack));
  document.getElementById('c-v')!.textContent = rupees(e.hoursValue);
}
calc.addEventListener('input', (e) => {
  const t = e.target as HTMLInputElement;
  if (t.dataset.k) (firm as unknown as Record<string, number>)[t.dataset.k] = Math.max(0, Number(t.value) || 0);
  if (t.dataset.t) firm[t.dataset.t as 'teams'] = t.checked;
  calc.querySelectorAll('.presets button').forEach((b) => b.classList.remove('on'));
  paint();
});
calc.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-preset]');
  if (!b) return;
  Object.assign(firm, FIRMS[b.dataset.preset as keyof typeof FIRMS]);
  calc.querySelectorAll('.presets button').forEach((x) => x.classList.toggle('on', x === b));
  paint();
});
paint();

/* ------------------------------------------------------------------ start */
window.addEventListener('resize', fit);
narrow.addEventListener('change', () => { fit(); show(at, false); });
window.addEventListener('hashchange', () => go(location.hash.slice(1)));
fit();
const first = slides.findIndex((s) => `#${s.id}` === location.hash);
show(first < 0 ? 0 : first, false);
if (reading() && first > 0) slides[first].scrollIntoView();
